import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@/utils/errorMessage';
import { SandboxService } from '../sandbox.service';
import {
	SANDBOX_SCENARIOS_QUERY_KEY,
	sandboxScenarioQueryKey,
} from '../queries/sandbox';

/**
 * Mutations do sandbox. Toda ação sobre um cenário invalida o detalhe do
 * cenário (estado vivo + linha do tempo) e a lista (contadores).
 */
const useInvalidateScenario = () => {
	const queryClient = useQueryClient();
	return (scenarioId?: string) => {
		queryClient.invalidateQueries({
			queryKey: SANDBOX_SCENARIOS_QUERY_KEY,
		});
		if (scenarioId) {
			queryClient.invalidateQueries({
				queryKey: sandboxScenarioQueryKey(scenarioId),
			});
		}
	};
};

export const useCreateSandboxScenario = (
	onSuccess?: (scenario: Sandbox.Scenario) => void,
) => {
	const invalidate = useInvalidateScenario();
	return useMutation({
		mutationFn: (body: Sandbox.CreateScenario) =>
			SandboxService.createScenario(body),
		onSuccess: (scenario) => {
			invalidate();
			toast.success('Cenário criado!');
			onSuccess?.(scenario);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao criar cenário'));
		},
	});
};

export const useDeleteSandboxScenario = (
	onSuccess?: (report: Sandbox.PurgeReport) => void,
) => {
	const invalidate = useInvalidateScenario();
	return useMutation({
		mutationFn: (id: string) => SandboxService.deleteScenario(id),
		onSuccess: (report, id) => {
			invalidate(id);
			if (report.errors.length > 0) {
				toast.warn(
					`Cenário mantido: ${report.errors.length} entidade(s) não puderam ser apagadas.`,
				);
			} else {
				toast.success('Cenário e dados de teste apagados.');
			}
			onSuccess?.(report);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao apagar cenário'));
		},
	});
};

export const useResetSandboxScenario = (
	onSuccess?: (report: Sandbox.PurgeReport) => void,
) => {
	const invalidate = useInvalidateScenario();
	return useMutation({
		mutationFn: (id: string) => SandboxService.resetScenario(id),
		onSuccess: (report, id) => {
			invalidate(id);
			if (report.errors.length > 0) {
				toast.warn(
					`${report.errors.length} entidade(s) não puderam ser apagadas.`,
				);
			} else {
				toast.success('Dados do cenário apagados.');
			}
			onSuccess?.(report);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao limpar cenário'));
		},
	});
};

export const useSandboxCreateUser = (
	scenarioId: string,
	onSuccess?: (user: Sandbox.CreatedUser) => void,
) => {
	const invalidate = useInvalidateScenario();
	return useMutation({
		mutationFn: (body: Sandbox.CreateUser) =>
			SandboxService.createUser(scenarioId, body),
		onSuccess: (user) => {
			invalidate(scenarioId);
			toast.success(`Usuário ${user.first_name} criado.`);
			onSuccess?.(user);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao criar usuário'));
		},
	});
};

export const useSandboxCreateProfessional = (
	scenarioId: string,
	onSuccess?: (professional: Sandbox.CreatedProfessional) => void,
) => {
	const invalidate = useInvalidateScenario();
	return useMutation({
		mutationFn: (body: Sandbox.CreateProfessional) =>
			SandboxService.createProfessional(scenarioId, body),
		onSuccess: (professional) => {
			invalidate(scenarioId);
			toast.success(`Profissional ${professional.first_name} criado.`);
			onSuccess?.(professional);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao criar profissional'));
		},
	});
};

export const useSandboxActivateSubscription = (
	scenarioId: string,
	onSuccess?: () => void,
) => {
	const invalidate = useInvalidateScenario();
	return useMutation({
		mutationFn: (body: Sandbox.ActivateSubscription) =>
			SandboxService.activateSubscription(scenarioId, body),
		onSuccess: () => {
			invalidate(scenarioId);
			toast.success('Assinatura ativada (pagamento simulado).');
			onSuccess?.();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao ativar assinatura'));
		},
	});
};

export const useSandboxCancelSubscription = (
	scenarioId: string,
	onSuccess?: () => void,
) => {
	const invalidate = useInvalidateScenario();
	return useMutation({
		mutationFn: (subscriptionId: string) =>
			SandboxService.cancelSubscription(scenarioId, subscriptionId),
		onSuccess: () => {
			invalidate(scenarioId);
			toast.success('Assinatura cancelada.');
			onSuccess?.();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao cancelar assinatura'));
		},
	});
};

export const useSandboxScheduleSession = (
	scenarioId: string,
	onSuccess?: (session: Sandbox.ScenarioSession) => void,
) => {
	const invalidate = useInvalidateScenario();
	return useMutation({
		mutationFn: (body: Sandbox.ScheduleSession) =>
			SandboxService.scheduleSession(scenarioId, body),
		onSuccess: (session) => {
			invalidate(scenarioId);
			toast.success(`Sessão agendada (${session.status}).`);
			onSuccess?.(session);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao agendar sessão'));
		},
	});
};

type SessionAction =
	| { type: 'pay'; sessionId: string; mode: 'MANUAL' | 'BENEFIT' }
	| { type: 'start'; sessionId: string; timeTravel?: boolean }
	| { type: 'end'; sessionId: string; timeTravel?: boolean }
	| { type: 'cancel'; sessionId: string; by: Sandbox.CancelActor };

const SESSION_ACTION_LABEL: Record<SessionAction['type'], string> = {
	pay: 'Pagamento confirmado.',
	start: 'Sessão iniciada.',
	end: 'Sessão finalizada.',
	cancel: 'Sessão cancelada.',
};

export const useSandboxSessionAction = (
	scenarioId: string,
	onSuccess?: (session: Sandbox.ScenarioSession) => void,
) => {
	const invalidate = useInvalidateScenario();
	return useMutation({
		mutationFn: (action: SessionAction) => {
			switch (action.type) {
				case 'pay':
					return SandboxService.paySession(
						scenarioId,
						action.sessionId,
						action.mode,
					);
				case 'start':
					return SandboxService.startSession(
						scenarioId,
						action.sessionId,
						action.timeTravel ?? true,
					);
				case 'end':
					return SandboxService.endSession(
						scenarioId,
						action.sessionId,
						action.timeTravel ?? true,
					);
				case 'cancel':
					return SandboxService.cancelSession(
						scenarioId,
						action.sessionId,
						action.by,
					);
			}
		},
		onSuccess: (session, action) => {
			invalidate(scenarioId);
			toast.success(SESSION_ACTION_LABEL[action.type]);
			onSuccess?.(session);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'A ação na sessão falhou'));
		},
	});
};

export const useSandboxSubmitMatch = (
	scenarioId: string,
	onSuccess?: (result: Match.Result) => void,
) => {
	const invalidate = useInvalidateScenario();
	return useMutation({
		mutationFn: (body: Sandbox.SubmitMatch) =>
			SandboxService.submitMatch(scenarioId, body),
		onSuccess: (result) => {
			invalidate(scenarioId);
			toast.success('Match respondido.');
			onSuccess?.(result);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao responder o match'));
		},
	});
};

export const useSandboxCreateCompany = (
	scenarioId: string,
	onSuccess?: (result: Sandbox.CreateCompanyResult) => void,
) => {
	const invalidate = useInvalidateScenario();
	return useMutation({
		mutationFn: (body: Sandbox.CreateCompany) =>
			SandboxService.createCompany(scenarioId, body),
		onSuccess: (result) => {
			invalidate(scenarioId);
			toast.success(`Empresa ${result.company.trade_name} criada.`);
			for (const warning of result.warnings) toast.warn(warning);
			onSuccess?.(result);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao criar empresa'));
		},
	});
};

export const useSandboxRunJourney = (
	scenarioId: string,
	onSuccess?: (result: Sandbox.JourneyResult) => void,
) => {
	const invalidate = useInvalidateScenario();
	return useMutation({
		mutationFn: (body: Sandbox.RunJourney) =>
			SandboxService.runJourney(scenarioId, body),
		onSuccess: (result) => {
			invalidate(scenarioId);
			if (result.completed) toast.success('Jornada concluída!');
			else toast.warn('Jornada interrompida — veja o passo que falhou.');
			onSuccess?.(result);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao rodar a jornada'));
		},
	});
};

export const useSandboxImpersonate = (
	scenarioId: string,
	onSuccess?: (result: Sandbox.Impersonation) => void,
) => {
	const invalidate = useInvalidateScenario();
	return useMutation({
		mutationFn: (body: { kind: Sandbox.ImpersonationKind; id: string }) =>
			SandboxService.impersonate(scenarioId, body),
		onSuccess: (result) => {
			invalidate(scenarioId);
			onSuccess?.(result);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao gerar link de acesso'));
		},
	});
};
